/**
 * 用户接口
 */
import request, { MethodEnum, serverTriggerUrl } from '@/utils/fetch';
import { IAvatar } from './common';
import { PERMISSION_APIS } from './permission';
import { PromiseState } from '@/constants';

/** 签到接口Response */
interface SignApiResponse {
  days: number;
  mdays: number;
  lasted: number;
  signed: boolean;
  success: boolean; 
  increased: number;
  /** 签到成功后后端返回的最新积分 */
  credits?: number;
}

export enum ACCOUNT_ROLE {
  USER = 'USER', // 普通用户
  ADMIN = 'ADMIN', // 管理员
  FORUM_ADMIN = 'FORUM_ADMIN' // 版主
}

export enum USER_APIS {
  USERS_INFO_LIST = '/users/login-info',
  USERS_INFO = '/users/info',
  LOGIN_OUT = '/auth/logout',
}

/** interfaces */
export interface IUserLoginInfo {
  uuid: string;
  name: string;
  messages: number;
  id: number;
  avatar: IAvatar;
  role: ACCOUNT_ROLE;
  /** /users/login-info 返回：今天是否已签到 */
  signed?: boolean;
  /** /users/login-info 返回：飞行里程是否隐藏（true=隐藏，false=可见） */
  hide_distance?: boolean;
  status?: string;
  /** /users/login-info 返回：是否可以访问主s页（包括自己和他人） */
  home_page_accessible_by_self?: boolean;
}

export interface IUserPermission {
  status: PromiseState;
  code?: number;
  message?: string;
}

export const getUserLoginInfo = (isServerTrigger = false, headers = {}, requestTimestamp?: number) => {
  const key = 'GetUserLoginInfo';
  const prefix = isServerTrigger ? serverTriggerUrl : '';
  const url = `${prefix}${USER_APIS.USERS_INFO_LIST}`;
  return request({ url, key, isServerTrigger, headers, params: { t: requestTimestamp ?? new Date().valueOf() } }).then((data: any) => {
    return data.item;
  }).catch((e)=>{
    return Promise.reject(e);
  });
};

export interface IUserGroupInfo {
  title: string;
  icon: string;
}

export interface IUserInfo {
  id: number;
  uuid: string;
  name: string;
  /** /users/info 返回的是字符串头像地址 */
  avatar: string;
  /** 管理员查看用户资料的额外字段 */
  admin_view?: {
    uid?: string | number;
    email?: string;
    register_ip?: string;
    register_location?: string;
    last_login_ip?: string;
    last_login_location?: string;
    last_login_time?: string;
    ban_expiry?: string | null;
  };
  /** 积分 */
  credits: number;
  /** 设备列表 */
  devices: any[];
  /** 用户组 */
  group: IUserGroupInfo;
  hide_distance: boolean;
  is_friend: boolean;
  rank: any;
  signature: string | null;
  total_distance: number;
  /** /users/info 返回：是否允许他人访问主页 */
  home_page_accessible_by_others?: boolean;
  login_credit_added?: boolean;
  visit_credit_added?: boolean;
}

export type IUserCenterInitInfo = IUserInfo;

export const getUserCenterInitInfo = (
  params: { uid: number; uuid?: string },
  isServerTrigger = false,
  headers = {},
): Promise<IUserCenterInitInfo> => {
  const key = 'GetUserCenterInitInfo';
  const prefix = isServerTrigger ? serverTriggerUrl : '';
  const url = `${prefix}${USER_APIS.USERS_INFO}`;
  return request({
    url,
    key,
    isServerTrigger,
    headers,
    method: MethodEnum.GET,
    params: {
      uid: params.uid,
      ...(params.uuid ? { uuid: params.uuid } : {}),
    },
  }).then((data: any) => {
    // 兼容后端返回 data.item 或 data.data 结构
    return data?.item ?? data;
  }).catch((e)=>{
    return Promise.reject(e);
  });
};

export const loginOut = () => {
  const url = USER_APIS.LOGIN_OUT;
  return request({ url }).then((data: any) => {
    return data;
  }).catch((e)=>{
    return Promise.reject(e);
  });
}

export const getUserPermission = () => {
  const url = PERMISSION_APIS.PERMISSION_CHECK;
  return request({
    url,
    method: MethodEnum.POST,
  }).then((data: {item?: string}) => {
    return data.item;
  }).catch((e)=>{
    return Promise.reject(e);
  });
};

/** 用户签到 */
export const userSign = () => {
  return request({
    // baseURL 已是 /api/v2；这里补齐 mobile 端参数
    url: '/home/paulsigns/sign?device=mobile',
    method: MethodEnum.POST,
  }).then((data: {item: SignApiResponse}) => {
    return data.item;
  }).catch((e)=>{
    return Promise.reject(e);
  });
};

export enum USER_CREDITS_APIS {
  SUMMARY = '/users/credits/summary',
  LOGS = '/users/credits/logs',
  EXCHANGE_CAPTCHA_CONFIG = '/users/credits/exchange/captcha/config',
  EXCHANGE_APPLY = '/users/credits/exchange/apply',
}

export interface IUserCreditsSummary {
  availableCredits: number;
  pendingCredits: number;
  exchangedCredits: number;
}

export interface IUserCreditsLogsItem {
  type: string;
  time: number;
  changeCredits: number;
  availableCredits: number;
  status?: string;
}

export interface IUserCreditsLogsRes {
  list: IUserCreditsLogsItem[];
  total: number;
}

export interface IUserCreditsCaptchaConfig {
  enabled: boolean;
  prefix: string;
  sceneH5: string;
  scenePc: string;
  sceneType: string;
}

export interface IUserCreditsExchangeApplyReq {
  credits: number;
  scene?: string;
  captcha_verify_param?: string;
}

export const getUserCreditsSummary = (): Promise<IUserCreditsSummary> => {
  return request({
    url: USER_CREDITS_APIS.SUMMARY,
    method: MethodEnum.GET,
  }).then((data: any) => {
    const item = data?.item || data;
    return {
      availableCredits: item?.availableCredits || 0,
      pendingCredits: item?.pendingCredits || 0,
      exchangedCredits: item?.exchangedCredits || 0,
    };
  }).catch((e) => {
    return Promise.reject(e);
  });
};

export const getUserCreditsLogs = (
  params: { page: number; page_size: number }
): Promise<IUserCreditsLogsRes> => {
  return request({
    url: USER_CREDITS_APIS.LOGS,
    method: MethodEnum.GET,
    params,
  }).then((data: any) => {
    const item = data?.item || data;
    const list = Array.isArray(item?.list) ? item.list : [];
    const total = typeof item?.total === 'number' ? item.total : 0;
    return {
      list,
      total,
    };
  }).catch((e) => {
    return Promise.reject(e);
  });
};

export const getUserCreditsExchangeCaptchaConfig = (): Promise<IUserCreditsCaptchaConfig> => {
  return request({
    url: USER_CREDITS_APIS.EXCHANGE_CAPTCHA_CONFIG,
    method: MethodEnum.GET,
  }).then((data: any) => {
    const item = data?.item || data || {};
    const prefix = item['aliyun.prefix'] || item.appKey || '';
    const sceneH5 = item['aliyun.scene.h5'] || item.scene || '';
    const scenePc = item['aliyun.scene.pc'] || item.scene || '';
    const sceneType = item['aliyun.scene.type'] || '';
    return {
      enabled: !!item.enabled,
      prefix,
      sceneH5,
      scenePc,
      sceneType,
    };
  }).catch((e) => {
    return Promise.reject(e);
  });
};

export const applyUserCreditsExchange = (params: IUserCreditsExchangeApplyReq): Promise<any> => {
  return request({
    url: USER_CREDITS_APIS.EXCHANGE_APPLY,
    method: MethodEnum.POST,
    params,
  }).then((data: any) => {
    return data;
  }).catch((e) => {
    return Promise.reject(e);
  });
};
